/**
 * @file Calendario — restyled to match design page-calendario.jsx.
 *
 * Layout:
 *  - pg-head + cal-toolbar (prev/today/next + range + view-segment)
 *  - cal-grid (week / 3day / day) with hour-rows + day-columns,
 *    events absolutely positioned via cal-event with eventStyle()
 *  - or month grid (7 columns × 5 rows)
 *
 * Uses design CSS classes from src/client/styles/cd.css.
 */
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppointments } from "../hooks/useAppointments";

const SVG = { fill: "none", stroke: "currentColor", strokeLinecap: "round", strokeLinejoin: "round" };

const I = {
  ChevL: () => (<svg width="14" height="14" viewBox="0 0 24 24" {...SVG} strokeWidth="1.8"><path d="M15 6l-6 6 6 6"/></svg>),
  ChevR: () => (<svg width="14" height="14" viewBox="0 0 24 24" {...SVG} strokeWidth="1.8"><path d="M9 6l6 6-6 6"/></svg>),
};

const HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
const HOUR_PX = 60;

const DOW_NAMES = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const MONTH_NAMES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};

const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const sameYMD = (a, b) => ymd(a) === ymd(b);

const formatTime = (iso) =>
  new Date(iso).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", hour12: false });

function eventStyle(startIso, endIso) {
  const s = new Date(startIso);
  const e = new Date(endIso);
  const startMins = (s.getHours() - HOURS[0]) * 60 + s.getMinutes();
  const endMins = (e.getHours() - HOURS[0]) * 60 + e.getMinutes();
  return {
    top: (startMins / 60) * HOUR_PX + "px",
    height: ((endMins - startMins) / 60) * HOUR_PX + "px",
  };
}

export default function CalendarView() {
  const navigate = useNavigate();
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });

  const { weekDays, rangeStart, rangeEnd } = useMemo(() => {
    const start = getWeekStart(anchor);
    const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
    return { weekDays: days, rangeStart: start, rangeEnd: addDays(start, 6) };
  }, [anchor]);

  const filterRange = useMemo(() => {
    if (view === "month") {
      return {
        from: new Date(anchor.getFullYear(), anchor.getMonth(), 1),
        to: new Date(anchor.getFullYear(), anchor.getMonth() + 2, 0),
      };
    }
    return { from: rangeStart, to: addDays(rangeEnd, 1) };
  }, [view, anchor, rangeStart, rangeEnd]);

  const { appointments } = useAppointments({
    autoFetch: true,
    filters: { dateFrom: filterRange.from.toISOString(), dateTo: filterRange.to.toISOString() },
  });

  const visibleAppts = useMemo(
    () => appointments.filter((a) => a.status !== "CANCELADA"),
    [appointments]
  );

  const todayDate = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const days =
    view === "day"
      ? [anchor]
      : view === "3day"
        ? [0, 1, 2].map((i) => addDays(anchor, i))
        : weekDays;

  const gridClass = view === "day" ? "days-1" : view === "3day" ? "days-3" : "";

  const range = useMemo(() => {
    if (view === "month") {
      return `${MONTH_NAMES[anchor.getMonth()]} ${anchor.getFullYear()}`;
    }
    if (view === "day") {
      return anchor.toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    }
    if (view === "3day") {
      const last = addDays(anchor, 2);
      return `${anchor.getDate()} – ${last.getDate()} ${MONTH_NAMES[last.getMonth()]}`;
    }
    return `${rangeStart.getDate()} – ${rangeEnd.getDate()} ${MONTH_NAMES[rangeEnd.getMonth()]} ${rangeEnd.getFullYear()}`;
  }, [view, anchor, rangeStart, rangeEnd]);

  const goPrev = () => {
    if (view === "month") setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1));
    else if (view === "day") setAnchor(addDays(anchor, -1));
    else if (view === "3day") setAnchor(addDays(anchor, -3));
    else setAnchor(addDays(anchor, -7));
  };
  const goNext = () => {
    if (view === "month") setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1));
    else if (view === "day") setAnchor(addDays(anchor, 1));
    else if (view === "3day") setAnchor(addDays(anchor, 3));
    else setAnchor(addDays(anchor, 7));
  };
  const goToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    setAnchor(d);
  };

  const now = new Date();
  const nowTop = ((now.getHours() - HOURS[0]) + now.getMinutes() / 60) * HOUR_PX;

  return (
    <>
      <div className="pg-head">
          <div>
            <div className="cd-eyebrow">Vista de calendario</div>
            <h1 className="pg-title"><em>Calendario</em></h1>
            <p className="pg-sub">Visualiza tu agenda completa por día, semana o mes.</p>
          </div>
        </div>

        <div className="cal-toolbar">
          <div className="cal-nav">
            <button type="button" className="cal-nav-btn" onClick={goPrev} aria-label="Anterior">
              <I.ChevL />
            </button>
            <button type="button" className="pg-btn" onClick={goToday}>Hoy</button>
            <button type="button" className="cal-nav-btn" onClick={goNext} aria-label="Siguiente">
              <I.ChevR />
            </button>
            <h2 className="cal-range">{range.charAt(0).toUpperCase() + range.slice(1)}</h2>
          </div>
          <div className="cal-view-seg">
            <button type="button" className={view === "day" ? "active" : ""} onClick={() => setView("day")}>Día</button>
            <button type="button" className={view === "3day" ? "active" : ""} onClick={() => setView("3day")}>3 Días</button>
            <button type="button" className={view === "week" ? "active" : ""} onClick={() => setView("week")}>Semana</button>
            <button type="button" className={view === "month" ? "active" : ""} onClick={() => setView("month")}>Mes</button>
          </div>
          <div className="cal-view-menu">
            <select className="form-select" value={view} onChange={(e) => setView(e.target.value)}>
              <option value="day">Día</option>
              <option value="3day">3 Días</option>
              <option value="week">Semana</option>
              <option value="month">Mes</option>
            </select>
          </div>
        </div>

        {view === "month" ? (
          <MonthView anchor={anchor} appointments={visibleAppts} />
        ) : (
          <div className={"cal-grid " + gridClass}>
            <div className="cal-corner"></div>
            {days.map((d) => {
              const isToday = sameYMD(d, todayDate);
              const isWeekend = d.getDay() === 0 || d.getDay() === 6;
              const dayCount = visibleAppts.filter((a) => sameYMD(new Date(a.dateTime), d)).length;
              const dow = DOW_NAMES[d.getDay()];
              return (
                <div key={ymd(d)} className={"cal-day-head" + (isToday ? " today" : "") + (isWeekend ? " weekend" : "")}>
                  <div className="dow">
                    <span className="full">{dow}</span>
                    <span className="abbr">{dow[0]}</span>
                  </div>
                  <div className="num">{d.getDate()}</div>
                  {dayCount > 0 && (
                    <div className="cita-count"><span className="dot" />{dayCount}</div>
                  )}
                </div>
              );
            })}
            <div className="cal-time-col">
              {HOURS.map((h) => (
                <div key={h} className="cal-time-cell">
                  {String(h).padStart(2, "0")}:00
                </div>
              ))}
            </div>
            {days.map((d) => {
              const isToday = sameYMD(d, todayDate);
              const isWeekend = d.getDay() === 0 || d.getDay() === 6;
              const events = visibleAppts.filter((a) => sameYMD(new Date(a.dateTime), d));
              return (
                <div
                  key={ymd(d)}
                  className={"cal-day-col" + (isToday ? " today" : "") + (isWeekend ? " weekend" : "")}
                >
                  {HOURS.map((h) => (
                    <React.Fragment key={h}>
                      <div className="cal-half-hour" />
                      <div className="cal-half-hour full" />
                    </React.Fragment>
                  ))}
                  {isToday && nowTop >= 0 && nowTop <= HOURS.length * HOUR_PX && (
                    <div className="cal-now-line" style={{ top: nowTop + "px" }} />
                  )}
                  {events.map((e) => {
                    const cls = e.status === "COMPLETADA" ? "done" : e.status === "ASISTENCIA_CONFIRMADA" ? "cur" : "";
                    const fullName = `${e.patient?.user?.firstName || ""} ${e.patient?.user?.lastName || ""}`.trim();
                    const inits = fullName.split(" ").filter(Boolean).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
                    return (
                      <div
                        key={e.id}
                        className={"cal-event " + cls}
                        style={eventStyle(e.dateTime, e.endTime)}
                        onClick={() => navigate(`/cita/${e.id}`)}
                      >
                        <div className="t">{formatTime(e.dateTime)}</div>
                        <div className="n" data-initials={inits}>{fullName || "Cita"}</div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

        <div style={{ display: "flex", gap: 18, marginTop: 18, fontSize: 12, color: "var(--ink-3)", flexWrap: "wrap" }}>
          <LegendDot color="oklch(0.55 0.15 260)" label="Agendada" />
          <LegendDot color="var(--accent)" label="En curso" />
          <LegendDot color="oklch(0.55 0.15 155)" label="Completada" />
        </div>
    </>
  );
}

function LegendDot({ color, label }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
      <span style={{ width: 10, height: 10, borderRadius: 3, background: color, display: "inline-block" }} />
      {label}
    </span>
  );
}

function MonthView({ anchor, appointments }) {
  const navigate = useNavigate();
  const monthStart = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const monthEnd = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // First Monday before or on the 1st
  const startDow = monthStart.getDay();
  const leadingDays = startDow === 0 ? 6 : startDow - 1;
  const gridStart = addDays(monthStart, -leadingDays);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));

  const dowLabels = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
  return (
    <div className="cal-month">
      <div className="cal-month-head">
        {dowLabels.map((d) => (
          <div key={d} className="cal-month-dow">
            <span className="full">{d}</span>
            <span className="abbr">{d[0]}</span>
          </div>
        ))}
      </div>
      <div className="cal-month-body">
        {cells.map((d, i) => {
          const dim = d < monthStart || d > monthEnd;
          const isToday = sameYMD(d, today);
          const events = appointments.filter((a) => sameYMD(new Date(a.dateTime), d));
          return (
            <div
              key={i}
              className={"cal-month-cell" + (dim ? " dim" : "") + (isToday ? " today" : "")}
            >
              <div className="num">{d.getDate()}</div>
              {events.length > 0 && (
                <div className="cal-month-events">
                  {/* Compact (very narrow): single count badge */}
                  <div className="cal-month-count">
                    <span className="dot" />{events.length}
                  </div>
                  {/* Mid: time-only chips */}
                  <div className="cal-month-chips">
                    {events.slice(0, 3).map((e) => (
                      <div key={e.id} className="cal-month-chip">{formatTime(e.dateTime)}</div>
                    ))}
                    {events.length > 3 && (
                      <div className="cal-month-more">+{events.length - 3}</div>
                    )}
                  </div>
                  {/* Wide: time + first name */}
                  <div className="cal-month-rows">
                    {events.slice(0, 3).map((e) => {
                      const first = e.patient?.user?.firstName || "Cita";
                      return (
                        <div
                          key={e.id}
                          className="cal-month-row"
                          onClick={() => navigate(`/cita/${e.id}`)}
                          style={{ cursor: "pointer" }}
                        >
                          <span className="t">{formatTime(e.dateTime)}</span>
                          <span className="n">{first}</span>
                        </div>
                      );
                    })}
                    {events.length > 3 && (
                      <div className="cal-month-more">+{events.length - 3} más</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
